import { Box } from '@chakra-ui/react'
import { MDXProvider } from '@mdx-js/react'

import { Footer } from './Footer.js'
import { Header } from './Header.js'
import { Link } from '../components/Link.js'
import { SafeContainer } from './SafeContainer.js'

/**
 * Styles inspired by https://github.com/tailwindlabs/tailwindcss-typography
 */

const components = {
  a: props => <Link {...props} />
}

export const Mdx = ({ children, ...rest }) => {
  return (
    <MDXProvider components={components}>
      <Box dir='ltr' {...rest}>
        <Header maxWidth='3xl' mb={10} />
        <SafeContainer maxWidth='3xl' className='prose'>
          {children}
        </SafeContainer>
        <Footer maxWidth='3xl' mt={10} mb={5} />
      </Box>

      <style jsx global>
        {`
          .prose [class~='lead'] {
            color: var(--chakra-colors-whiteAlpha-00);
            font-size: 1.25em;
            line-height: 1.6;
            margin-top: 1.2em;
            margin-bottom: 1.2em;
          }

          .prose strong {
            font-weight: 800;
          }

          .prose a {
            text-decoration: underline;
            font-weight: 500;
          }

          .prose ol[type='A'] {
            --list-counter-style: upper-alpha;
          }

          .prose ol[type='a'] {
            --list-counter-style: lower-alpha;
          }

          .prose ol[type='A s'] {
            --list-counter-style: upper-alpha;
          }

          .prose ol[type='a s'] {
            --list-counter-style: lower-alpha;
          }

          .prose ol[type='I'] {
            --list-counter-style: upper-roman;
          }

          .prose ol[type='i'] {
            --list-counter-style: lower-roman;
          }

          .prose ol[type='I s'] {
            --list-counter-style: upper-roman;
          }

          .prose ol[type='i s'] {
            --list-counter-style: lower-roman;
          }

          .prose ol[type='1'] {
            --list-counter-style: decimal;
          }

          .prose ol > li {
            list-style-type: none;
            position: relative;
            padding-left: 1.75em;
          }

          .prose ol > li::before {
            content: counter(list-item, var(--list-counter-style, decimal)) '.';
            position: absolute;
            font-weight: 400;
            color: var(--chakra-colors-whiteAlpha-800);
            left: 0;
          }

          .prose ul > li {
            list-style-type: none;
            position: relative;
            padding-left: 1.75em;
          }

          .prose ul > li::before {
            content: '';
            position: absolute;
            background-color: var(--chakra-colors-whiteAlpha-800);
            border-radius: 50%;
            width: 0.375em;
            height: 0.375em;
            top: calc(0.875em - 0.1875em);
            left: 0.25em;
          }

          .prose hr {
            border-color: var(--chakra-colors-whiteAlpha-900);
            border-top-width: 1px;
            margin-top: 3em;
            margin-bottom: 3em;
          }

          .prose blockquote {
            font-weight: 500;
            font-style: italic;
            color: var(--chakra-colors-whiteAlpha-900);
            border-left-width: 0.25rem;
            border-left-color: var(--chakra-colors-whiteAlpha-900);
            quotes: '“' '”' '‘' '’';
            margin-top: 1.6em;
            margin-bottom: 1.6em;
            padding-left: 1em;
          }

          .prose blockquote p:first-of-type::before {
            content: open-quote;
          }

          .prose blockquote p:last-of-type::after {
            content: close-quote;
          }

          .prose h1 {
            color: var(--chakra-colors-whiteAlpha-900);
            font-weight: 800;
            font-size: 2.25em;
            margin-top: 0;
            margin-bottom: 0.8888889em;
            line-height: 1.1111111;
          }

          .prose h2 {
            color: var(--chakra-colors-whiteAlpha-900);
            font-weight: 700;
            font-size: 1.5em;
            margin-top: 2em;
            margin-bottom: 1em;
            line-height: 1.3333333;
          }

          .prose h3 {
            color: var(--chakra-colors-whiteAlpha-900);
            font-weight: 600;
            font-size: 1.25em;
            margin-top: 1.6em;
            margin-bottom: 0.6em;
            line-height: 1.6;
          }

          .prose h4 {
            color: var(--chakra-colors-whiteAlpha-900);
            font-weight: 600;
            margin-top: 1.5em;
            margin-bottom: 0.5em;
            line-height: 1.5;
          }

          .prose figure figcaption {
            color: var(--chakra-colors-whiteAlpha-800);
            font-size: 0.875em;
            line-height: 1.4285714;
            margin-top: 0.8571429em;
          }

          .prose code {
            color: var(--chakra-colors-whiteAlpha-900);
            font-weight: 600;
            font-size: 0.875em;
          }

          .prose a code {
            color: var(--chakra-colors-whiteAlpha-900);
          }

          .prose pre {
            color: var(--chakra-colors-whiteAlpha-900);
            background-color: var(--chakra-colors-whiteAlpha-100);
            overflow-x: auto;
            font-size: 0.875em;
            line-height: 1.7142857;
            margin-top: 1.7142857em;
            margin-bottom: 1.7142857em;
            border-radius: 0.375rem;
            padding-top: 0.8571429em;
            padding-right: 1.1428571em;
            padding-bottom: 0.8571429em;
            padding-left: 1.1428571em;
          }

          .prose pre code {
            background-color: transparent;
            border-width: 0;
            border-radius: 0;
            padding: 0;
            font-weight: 400;
            color: inherit;
            font-size: inherit;
            font-family: inherit;
            line-height: inherit;
          }

          .prose pre code::before {
            content: none;
          }

          .prose pre code::after {
            content: none;
          }

          .prose table {
            width: 100%;
            table-layout: auto;
            text-align: left;
            margin-top: 2em;
            margin-bottom: 2em;
            font-size: 0.875em;
            line-height: 1.7142857;
          }

          .prose thead {
            color: var(--chakra-colors-whiteAlpha-900);
            font-weight: 600;
            border-bottom-width: 1px;
            border-bottom-color: var(--chakra-colors-whiteAlpha-800);
          }

          .prose thead th {
            vertical-align: bottom;
            padding-right: 0.5714286em;
            padding-bottom: 0.5714286em;
            padding-left: 0.5714286em;
          }

          .prose tbody tr {
            border-bottom-width: 1px;
            border-bottom-color: var(--chakra-colors-whiteAlpha-800);
          }

          .prose tbody tr:last-child {
            border-bottom-width: 0;
          }

          .prose tbody td {
            vertical-align: top;
            padding-top: 0.5714286em;
            padding-right: 0.5714286em;
            padding-bottom: 0.5714286em;
            padding-left: 0.5714286em;
          }

          .prose {
            font-size: 1rem;
            line-height: 1.75;
          }

          .prose p {
            margin-top: 1.25em;
            margin-bottom: 1.25em;
          }

          .prose img {
            margin-top: 2em;
            margin-bottom: 2em;
          }

          .prose video {
            margin-top: 2em;
            margin-bottom: 2em;
          }

          .prose figure {
            margin-top: 2em;
            margin-bottom: 2em;
          }

          .prose figure > * {
            margin-top: 0;
            margin-bottom: 0;
          }

          .prose h2 code {
            font-size: 0.875em;
          }

          .prose h3 code {
            font-size: 0.9em;
          }

          .prose ol {
            margin-top: 1.25em;
            margin-bottom: 1.25em;
          }

          .prose ul {
            margin-top: 1.25em;
            margin-bottom: 1.25em;
          }

          .prose li {
            margin-top: 0.5em;
            margin-bottom: 0.5em;
          }

          .prose > ul > li p {
            margin-top: 0.75em;
            margin-bottom: 0.75em;
          }

          .prose > ul > li > *:first-child {
            margin-top: 1.25em;
          }

          .prose > ul > li > *:last-child {
            margin-bottom: 1.25em;
          }

          .prose > ol > li > *:first-child {
            margin-top: 1.25em;
          }

          .prose > ol > li > *:last-child {
            margin-bottom: 1.25em;
          }

          .prose ul ul,
          .prose ul ol,
          .prose ol ul,
          .prose ol ol {
            margin-top: 0.75em;
            margin-bottom: 0.75em;
          }

          .prose hr + * {
            margin-top: 0;
          }

          .prose h2 + * {
            margin-top: 0;
          }

          .prose h3 + * {
            margin-top: 0;
          }

          .prose h4 + * {
            margin-top: 0;
          }

          .prose thead th:first-child {
            padding-left: 0;
          }

          .prose thead th:last-child {
            padding-right: 0;
          }

          .prose tbody td:first-child {
            padding-left: 0;
          }

          .prose tbody td:last-child {
            padding-right: 0;
          }

          .prose > :first-child {
            margin-top: 0;
          }

          .prose > :last-child {
            margin-bottom: 0;
          }
        `}
      </style>
    </MDXProvider>
  )
}
